import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { Pair, PairsControllerService } from '@api/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { toOptionItemModel } from '@shared/components/multi-select-with-exclude/multi-select-with-exclude.utils';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { getBaseAssetCode, getQuoteAssetCode } from '@shared/utils/assets';
import { isDefined } from '@stillmandigital/common';
import { keyBy } from 'lodash';
import { pipe, switchMap } from 'rxjs';

export interface PairViewModel extends Pair {
  baseAssetCode: string;
  quoteAssetCode: string;
}

export const usePairs = () =>
  signalStoreFeature(
    {
      props: {
        _notificationService: type<NotificationService>(),
      },
    },
    withState({
      pairs: new Array<Pair>(),
      pairsLoaded: false,
    }),
    withProps(() => ({
      _pairSettingsService: inject(PairsControllerService),
    })),
    withComputed(store => {
      const pairsViewModel = computed<PairViewModel[]>(() =>
        store.pairs().map(pair => ({
          ...pair,
          baseAssetCode: getBaseAssetCode(pair.name),
          quoteAssetCode: getQuoteAssetCode(pair.name),
        })),
      );

      const basePairs = computed(() => pairsViewModel().filter(pair => !isDefined(pair.basePairId)));

      const basePairOptions = computed(() => basePairs().map(basePair => toOptionItemModel(basePair, 'id', 'name')));

      const lpPairs = computed(() => pairsViewModel().filter(pair => isDefined(pair.basePairId)));

      const pairsMap = computed(() => keyBy(pairsViewModel(), 'id'));
      const basePairsMap = computed(() => keyBy(basePairs(), 'id'));

      const activeBasePairs = computed(() => basePairs().filter(pair => pair.active));

      const housePairs = computed(() => basePairs().filter(pair => !!pair.supportHouse));
      const houseUsdPairs = computed(() => housePairs().filter(pair => pair.name?.endsWith('/USD')));
      const housePairsOptions = computed(() =>
        houseUsdPairs().map(pair => toOptionItemModel(pair, 'baseAssetId', 'name')),
      );

      const lpPairsMap = computed(() => keyBy(lpPairs(), 'id'));

      return {
        basePairs,
        basePairOptions,
        lpPairs,
        activeBasePairs,
        pairsMap,
        basePairsMap,
        housePairs,
        houseUsdPairs,
        housePairsOptions,
        lpPairsMap,
      };
    }),
    withMethods(store => {
      const loadPairs = rxMethod<void>(
        pipe(
          switchMap(() =>
            store._pairSettingsService.getPairs().pipe(
              tapResponse({
                next: (pairs: Pair[]) => patchState(store, { pairs, pairsLoaded: true }),
                error: (error: HttpErrorResponse) => {
                  store._notificationService.showErrorNotification('Failed to load pairs', error);
                  patchState(store, { pairsLoaded: true });
                },
              }),
            ),
          ),
        ),
      );
      return {
        loadPairs,
      };
    }),
  );
